import {
  PropsWithChildren,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import {
  Channel,
  ChannelFilters,
  ChannelOptions,
  StreamChat,
} from "stream-chat";
import {
  ChannelListMessengerProps,
  ChannelPreviewUIComponentProps,
  DefaultStreamChatGenerics,
  MessageDeliveryStatus,
  ChannelList as StreamChatChannelList,
  Channel as StreamChatChannel,
  StreamMessage,
  Window,
  DateSeparatorProps,
  MessageList,
  MessageInput,
  MessageInputFlat,
  useMessageInputContext,
} from "stream-chat-react";
import { match, P } from "ts-pattern";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { Checkmark, CheckAll, Close } from "../assets/icons/16/outline";
import { Plus } from "../assets/icons/24/outline";
import { Empty } from "../assets/svg";
import { Avatar } from "../avatar";
import { Button, type Props as ButtonProps } from "../button/Button";
import { NumberPill, Pill } from "../pills";
import { Modal } from "../modal/Modal";
import { SearchInput } from "../search/SearchInput";
import { Headline, Label, Paragraph } from "../typography";
import {
  SortingOptions,
  useSortingOptions,
} from "../utils/hooks/useSortingOptions";
import { nullable } from "../utils/typeUtils";
import { LoadingBounce } from "../loading";
import { DateSettings, useDateFormatter } from "../utils/dateUtils";
import { MessageSimple } from "./Message";

export type User = { firstName: string | null; lastName: string | null };
export type UserDict = {
  [key: string]: User;
};

const ChannelListContainer = ({
  children,
}: PropsWithChildren<ChannelListMessengerProps>) => {
  return (
    <div className="channel-wrapper px-2 pb-4 pt-0.5 h-full overflow-y-auto [&>button:not(:first-of-type)]:border-t">
      {children}
    </div>
  );
};

const ChannelAvatar = ({
  users,
  loading,
}: {
  users: User[];
  loading: boolean;
}): React.ReactNode => {
  return (
    match({ loading, users })
      .with({ loading: true }, () => (
        <SkeletonTheme baseColor="#202020" highlightColor="#444">
          <Skeleton
            containerClassName="flex"
            circle={true}
            height={40}
            width={40}
          />
        </SkeletonTheme>
      ))
      // Single user
      .with({ loading: false, users: [P.select()] }, (singleUser) => (
        <Avatar
          firstName={singleUser.firstName ?? ""}
          lastName={singleUser.lastName ?? ""}
          randomizeColor
          size="lg"
        />
      ))
      // Multiple users
      .with(
        {
          loading: false,
          users: [
            P.select("primaryUser"),
            ...P.array(P.any).select("otherUsers"),
          ],
        },
        ({ primaryUser, otherUsers }) => (
          <div className="w-10 h-10 relative">
            <Avatar
              className="absolute"
              firstName={primaryUser.firstName ?? ""}
              lastName={primaryUser.lastName ?? ""}
              randomizeColor
              size="sm"
            />
            <Pill
              className="absolute bottom-0 right-0 max-w-6 justify-center ring-1 ring-greyscale-0 dark:ring-greyscale-800"
              color={"grey"}
              text={`+${otherUsers.length.toString()}`}
            />
          </div>
        ),
      )
      .otherwise(() => null)
  );
};

const ChannelTitle = ({
  users,
  loading,
  translationMap,
}: {
  users: User[];
  loading: boolean;
  translationMap: {
    and: string;
  };
}) => {
  const title: string = match(users)
    // Single user
    .with(
      [P.select()],
      (user) => `${user.firstName ?? ""} ${user.lastName ?? ""}`, // TODO put in nameorder here
    )
    // Multiple users
    .with(
      [...P.array(P.any).select("users"), P.select("lastUser")],
      ({ users, lastUser }) => {
        return `${users.map(({ firstName }) => firstName ?? "").join(", ")} ${translationMap.and} ${lastUser.firstName ?? ""}`; // TODO Put in nameorder here
      },
    )
    .otherwise(() => "-");
  return (
    <Label
      size="xs"
      className="text-greyscale-100 font-poppins truncate text-left"
    >
      {loading ? <Skeleton width={"50%"} /> : title}
    </Label>
  );
};

const LastMessageTimestamp = ({
  message,
  status,
  dateSettings,
}: {
  message: StreamMessage;
  status?: MessageDeliveryStatus;
  dateSettings: DateSettings;
}) => {
  const { formatTime, timezone } = useDateFormatter(dateSettings);
  const date = match(message.created_at)
    .with(P.string, (dateString) => {
      const date = new Date(dateString);
      // Make sure it's a valid date
      return isNaN(date.getTime()) ? undefined : date;
    })
    .with(P.instanceOf(Date), (date) => date)
    .otherwise(() => undefined);
  if (!date) return null;

  // Convert the date to the user's timezone before formatting
  const dateInUserTimezone = DateTime.fromJSDate(date).setZone(timezone);

  return (
    <div className="flex items-center gap-0.5">
      {match(status)
        .with(MessageDeliveryStatus.DELIVERED, () => (
          <Checkmark className="text-primary-600" />
        ))
        .with(MessageDeliveryStatus.READ, () => (
          <CheckAll className="text-primary-600" />
        ))
        .otherwise(() => null)}
      <Paragraph
        className="!text-greyscale-600 dark:!text-greyscale-400"
        size="xs"
      >
        {formatTime(dateInUserTimezone)}
      </Paragraph>
    </div>
  );
};

const ChannelPreview = ({
  userDict,
  loading,
  isChannelActive,
  onChannelSelect,
  translationMap,
  dateSettings,
  ...props
}: ChannelPreviewUIComponentProps & {
  userDict: UserDict;
  loading: boolean;
  isChannelActive: boolean;
  onChannelSelect: (id: string, event: React.MouseEvent) => void;
  translationMap: TranslationMap;
  dateSettings: DateSettings;
}) => {
  const {
    channel,
    lastMessage,
    latestMessagePreview,
    messageDeliveryStatus,
    unread,
  } = props;
  const { id } = channel;
  if (!id) return;
  // Get channel members, exluding the current user
  const otherChannelMembers = Object.keys(channel.state.members).filter(
    (id) => id !== channel._client.user?.id,
  );
  // Map channel members to our user dict, filter out any nullable values
  const users = otherChannelMembers.map((id) => userDict[id]).filter(nullable);
  // Check that no users are missing from the dict
  const dictMissingUsers = otherChannelMembers.length !== users.length;
  // If we don't have any data on members it probably means that
  // the chat client hasn't fully loaded all its data.
  const noChannelUsers = users.length === 0;
  // We want to show a loading indicator if any of the following conditions apply
  const loadingAnyRelevantData = loading || dictMissingUsers || noChannelUsers;
  return (
    <button
      className={classNames(
        "channel flex w-full px-2 py-4 gap-3 cursor-pointer border-greyscale-200 dark:border-greyscale-700",
        {
          // Remove borders on this and the next available sibling channel
          "border-transparent [&+button]:border-transparent": isChannelActive,
          // Style this channel to show it being active
          "bg-greyscale-0 dark:bg-greyscale-800 rounded-2xl": isChannelActive,
          "shadow hover:shadow-hover": isChannelActive,
        },
      )}
      data-testid="channel-preview-button"
      onClick={(event) => onChannelSelect(id, event)}
    >
      <div className="self-center">
        <ChannelAvatar loading={loadingAnyRelevantData} users={users} />
      </div>
      <div className="flex-1 flex flex-col items-start">
        <div className="grid grid-cols-[1fr_auto] gap-4 w-full">
          <ChannelTitle
            loading={loadingAnyRelevantData}
            users={users}
            translationMap={translationMap}
          />
          {unread && unread > 0 ? (
            <NumberPill count={unread} color="redSubdued" />
          ) : null}
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-4 w-full">
          <Paragraph
            className="!text-greyscale-600 dark:!text-greyscale-400 text-left !truncate"
            size="xs"
          >
            {match({
              lastMessage: lastMessage?.text ?? "",
              lastMessagePreview: latestMessagePreview,
            })
              .with(
                { lastMessage: P.when((value) => value.length > 0) },
                ({ lastMessage }) => lastMessage,
              )
              .with(
                {
                  lastMessage: "",
                  lastMessagePreview: P.when(
                    (value) => typeof value === "string" && value.length > 0,
                  ),
                },
                ({ lastMessagePreview }) => lastMessagePreview,
              )
              .otherwise(() => translationMap.noMessagePreview)}
          </Paragraph>
          {lastMessage && (
            <LastMessageTimestamp
              message={lastMessage}
              status={messageDeliveryStatus}
              dateSettings={dateSettings}
            />
          )}
        </div>
      </div>
    </button>
  );
};

const ChannelsLoading = ({
  count,
  translationMap,
}: {
  count: number;
  translationMap: TranslationMap;
}) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="channel flex w-full px-2 py-4 gap-3">
          <ChannelAvatar loading users={[]} />
          <ChannelTitle loading users={[]} translationMap={translationMap} />
        </div>
      ))}
    </>
  );
};

const ChannelsEmpty = ({
  translationMap,
}: {
  translationMap: TranslationMap;
}) => {
  return (
    <div className="flex flex-col gap-10 items-center justify-center h-full">
      <Empty.F3 />
      <Paragraph size="m">{translationMap.noMessages}</Paragraph>
    </div>
  );
};

// TODO: add tests
/**
 * @returns Returns an array of id's for any users we currently do not have in our dict
 */
export const getListOfUnfetchedUsers = (
  usersInState: UserDict,
  usersFromChat: string[],
): string[] => {
  const userIdsInState = new Set(Object.keys(usersInState));
  return usersFromChat.filter((id) => !userIdsInState.has(id));
};

export type FetchUsers = (ids: string[]) => void;

type TranslationMap = {
  and: string;
  noMessagePreview: string;
  noMessages: string;
};
type ChannelListProps = {
  client: StreamChat;
  userDict: UserDict;
  /**
   * This will only ask for users not already in the userDict
   * @param ids
   */
  fetchUsers: FetchUsers;
  userId: string;
  loading: boolean;
  /**
   * The id of the currently active channel
   */
  activeChannelId?: string;
  options?: ChannelOptions;
  // make required (and others)
  translationMap?: TranslationMap;
  /**
   * @param id id of the channel the user selected
   * @param event event attached to the onClick handler
   */
  onSelect: (id: string, event: React.MouseEvent) => void;
  dateSettings: DateSettings;
};
export const ChannelList = ({
  client,
  userDict,
  fetchUsers,
  userId,
  loading,
  activeChannelId,
  onSelect,
  options = { presence: true, state: true },
  translationMap = {
    and: "and",
    noMessagePreview: "Nothing yet...",
    noMessages: "No messages",
  },
  dateSettings,
}: ChannelListProps) => {
  const [loadingChannels, setLoadingChannels] = useState(true);
  const filters: ChannelFilters<DefaultStreamChatGenerics> = useMemo(
    () => ({
      members: { $in: [userId] },
      type: "messaging",
    }),
    [userId],
  );
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        await client.queryChannels(filters);
      } catch (error) {
        console.error("error fetching chats: ", error);
      } finally {
        setTimeout(() => setLoadingChannels(false), 100);
      }
    };
    fetchChannels();
  }, [client, filters]);

  useEffect(() => {
    const {
      state: { users: clientUsers },
    } = client;
    // Get all other users, exluding the current user
    const otherUserIds = Object.keys(clientUsers).filter((id) => id !== userId);
    // Filter out users not currently in the dict
    const unfetchedUserIds = getListOfUnfetchedUsers(userDict, otherUserIds);
    if (unfetchedUserIds.length > 0 && !loadingChannels) {
      // Fetch any missing users
      fetchUsers(unfetchedUserIds);
    }
    // no need for the function or dict to be here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingChannels, client, userId]);

  const loadingData = loading || loadingChannels;

  return (
    <StreamChatChannelList
      List={ChannelListContainer}
      sendChannelsToList
      Preview={(props) => (
        <ChannelPreview
          userDict={userDict}
          loading={loadingData}
          isChannelActive={activeChannelId === props.channel.id}
          translationMap={translationMap}
          onChannelSelect={onSelect}
          dateSettings={dateSettings}
          {...props}
        />
      )}
      LoadingIndicator={() => "loading"}
      EmptyStateIndicator={() =>
        loadingData ? (
          <ChannelsLoading count={5} translationMap={translationMap} />
        ) : (
          <ChannelsEmpty translationMap={translationMap} />
        )
      }
      filters={filters}
      options={options}
      setActiveChannelOnMount={false}
    />
  );
};

type State<T> = {
  showModal: boolean;
  search: string;
  selectedUsers: Set<T>;
};
type Action<T> =
  | { type: "RESET" }
  | { type: "SET_SHOW_MODAL"; value: boolean }
  | { type: "SET_SEARCH"; value: string }
  | { type: "ADD_SELECTED_USER"; value: T }
  | { type: "REMOVE_SELECTED_USER"; value: T };

const reducer = <T,>(state: State<T>, action: Action<T>): State<T> => {
  switch (action.type) {
    case "RESET":
      return {
        showModal: false,
        search: "",
        selectedUsers: new Set<T>(),
      };
    case "SET_SHOW_MODAL":
      return { ...state, showModal: action.value };
    case "SET_SEARCH":
      return { ...state, search: action.value };
    case "ADD_SELECTED_USER":
      return {
        ...state,
        search: "",
        selectedUsers: new Set([...state.selectedUsers, action.value]),
      };
    case "REMOVE_SELECTED_USER":
      return {
        ...state,
        search: "",
        selectedUsers: new Set(
          [...state.selectedUsers].filter((value) => value !== action.value),
        ),
      };
    default:
      return state;
  }
};

type Chatter = User & { id: string };
type CreateChatProps<T extends Chatter> = {
  text: string;
  onClick: (
    userIds: T[],
    event: React.MouseEvent<HTMLButtonElement>,
  ) => Promise<boolean>;
  userDict: UserDict;
  availableUsersForChat: T[];
  loadingAvailableUsers: boolean;
  creatingNewChannel?: boolean;
  sortingOptions: SortingOptions;
  translationMap: {
    to: string;
    noUsersFound: string;
    noUsersExist: string;
    startChat: string;
  };
  fetchUsers: FetchUsers;
};
export const CreateChatButton = <T extends Chatter>({
  variant = "primary",
  icon = Plus,
  iconPosition = "left",
  onClick,
  sortingOptions,
  userDict,
  availableUsersForChat,
  loadingAvailableUsers,
  creatingNewChannel = false,
  translationMap,
  fetchUsers,
  ...props
}: Omit<ButtonProps, "text" | "onClick"> & CreateChatProps<T>) => {
  const { nameOrderFn, collator } = useSortingOptions(sortingOptions);
  const [{ showModal, search, selectedUsers }, dispatch] = useReducer(
    reducer<T>,
    {
      showModal: false,
      search: "",
      selectedUsers: new Set<T>(),
    },
  );

  const sortedUsers = useMemo(() => {
    return [...availableUsersForChat].sort((a, b) => {
      const nameA = nameOrderFn(a);
      const nameB = nameOrderFn(b);
      if (nameA !== null && nameB !== null) {
        return collator.compare(nameA, nameB);
      } else return 0;
    });
  }, [availableUsersForChat, nameOrderFn, collator]);

  const filteredUsers = useMemo(() => {
    if (!search) return sortedUsers;

    return sortedUsers.filter((user) => {
      const name = nameOrderFn(user);
      if (name) {
        return name.toLocaleLowerCase().includes(search.toLocaleLowerCase());
      } else return false;
    });
  }, [search, sortedUsers, nameOrderFn]);

  useEffect(() => {
    const unfetchedUserIds = getListOfUnfetchedUsers(
      userDict,
      availableUsersForChat.map(({ id }) => id),
    );
    if (unfetchedUserIds.length > 0) {
      fetchUsers(unfetchedUserIds);
    }
  }, [availableUsersForChat, userDict, fetchUsers]);

  return (
    <>
      <Button
        variant={variant}
        icon={icon}
        iconPosition={iconPosition}
        onClick={() => {
          dispatch({ type: "SET_SHOW_MODAL", value: true });
        }}
        {...props}
      />
      <Modal
        show={showModal}
        fixedPosition="top"
        onClose={() => dispatch({ type: "RESET" })}
      >
        <div className="flex flex-col gap-6 max-h-full overflow-hidden h-[calc(100vh-92px)] sm:h-auto sm:max-h-[min(720px,calc(100vh-116px))]">
          <div className="flex gap-3 px-2">
            <div className="flex items-center h-12">
              <Label size="s">{translationMap.to}</Label>
            </div>
            <div className="flex-1 overflow-hidden">
              <SearchInput
                inputValue={search}
                setInputValue={(value) =>
                  dispatch({ type: "SET_SEARCH", value })
                }
              />
              {selectedUsers.size > 0 && (
                <div className="flex flex-wrap gap-2 mt-4 max-h-52 min-h-8 overflow-y-scroll">
                  {[...selectedUsers].map((user) => (
                    <Pill
                      size="md"
                      key={user.id}
                      color={"darkBlue"}
                      text={user.firstName ?? ""}
                      icon={
                        <button
                          className="flex items-center justify-center -ml-1 min-h-5 min-w-5"
                          onClick={() =>
                            dispatch({
                              type: "REMOVE_SELECTED_USER",
                              value: user,
                            })
                          }
                        >
                          <Close />
                        </button>
                      }
                      iconSide="right"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div
            className={classNames(
              "flex flex-col py-1 gap-1 overflow-y-auto flex-1 min-h-0",
              {
                "items-center justify-center gap-10":
                  filteredUsers.length === 0,
              },
            )}
          >
            {loadingAvailableUsers ? (
              <div className="flex items-center justify-center">
                <LoadingBounce size="m" />
              </div>
            ) : (
              match({ filteredUsers, search })
                .with({ filteredUsers: [], search: "" }, () => (
                  <>
                    <Empty.F3 />
                    <Paragraph size="m">
                      {translationMap.noUsersExist}
                    </Paragraph>
                  </>
                ))
                .with({ filteredUsers: [] }, () => (
                  <Paragraph size="m" type="secondary">
                    {translationMap.noUsersFound}
                  </Paragraph>
                ))
                .otherwise(() =>
                  filteredUsers.map((user) => {
                    const isSelected = selectedUsers.has(user);
                    return (
                      // Padding for focus outline
                      <div key={user.id} className="px-[1px]">
                        <button
                          className={classNames(
                            "flex items-center gap-3 justify-between w-full pl-2 pt-2 pr-4 pb-2 rounded-lg",
                            // stole these styles from ListItem and change focus to ring
                            "hover:bg-primary-50 focus:bg-primary-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-600",
                            // Dark mode list item hover
                            "dark:hover:bg-primary-900 dark:focus:bg-primary-900",
                            {
                              "bg-primary-100": isSelected,
                            },
                          )}
                          onClick={() =>
                            isSelected
                              ? dispatch({
                                  type: "REMOVE_SELECTED_USER",
                                  value: user,
                                })
                              : dispatch({
                                  type: "ADD_SELECTED_USER",
                                  value: user,
                                })
                          }
                        >
                          <div className="flex items-center gap-3 max-w-full w-full">
                            <Avatar
                              className="shrink-0"
                              firstName={user.firstName ?? ""}
                              lastName={user.lastName ?? ""}
                              randomizeColor
                            />
                            <Paragraph
                              size="m"
                              className="text-greyscale-900 dark:text-greyscale-100 !truncate max-w-full w-full text-start"
                            >
                              {nameOrderFn(user).trim()}
                            </Paragraph>
                            {isSelected && <Checkmark className="shrink-0" />}
                          </div>
                        </button>
                      </div>
                    );
                  }),
                )
            )}
          </div>
          {selectedUsers.size > 0 && (
            <div className="shrink-0 w-full">
              <Button
                variant="primary"
                text={translationMap.startChat}
                className="w-full"
                loading={creatingNewChannel}
                onClick={(event) => {
                  onClick &&
                    onClick([...selectedUsers], event).then((success) =>
                      success
                        ? dispatch({ type: "RESET" })
                        : console.log("error: unable to create chat"),
                    );
                }}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

const CustomDateSeparator = ({
  dateSettings,
  date,
}: { dateSettings: DateSettings } & DateSeparatorProps) => {
  const { formatDate } = useDateFormatter(dateSettings);

  const dateTime = DateTime.fromJSDate(date);

  return (
    <div className="flex justify-center items-center py-2">
      <div className="px-3 py-px rounded-lg justify-start items-center gap-2.5 inline-flex">
        <div className="text-center text-xs font-normal font-inclusive leading-none">
          {formatDate(dateTime)}
        </div>
      </div>
    </div>
  );
};

export const ChatHeader = ({
  client,
  channelId,
  userDict,
  fetchUsers,
  fetchingUsers,
  translationMap,
  className,
}: {
  client: StreamChat;
  channelId: string;
  userDict: UserDict;
  fetchUsers: FetchUsers;
  fetchingUsers: boolean;
  translationMap: {
    and: string;
  };
  className?: string;
}) => {
  const channel: Channel = client.channel("messaging", channelId);

  // Get channel members, exluding the current user
  const otherChannelMembers = useMemo(
    () =>
      Object.keys(channel.state.members).filter(
        (id) => id !== channel._client.user?.id,
      ),
    [channel._client.user?.id, channel.state.members],
  );
  // Map channel members to our user dict, filter out any nullable values
  const users = useMemo(
    () => otherChannelMembers.map((id) => userDict[id]).filter(nullable),
    [otherChannelMembers, userDict],
  );
  // Time since last message
  const lastMessageAt = useMemo(
    () =>
      typeof channel.data?.last_message_at === "string"
        ? DateTime.fromISO(channel.data.last_message_at).toRelative()
        : null,
    [channel.data?.last_message_at],
  );
  const loadingAnyRelevantData = useMemo(
    () => users.length === 0,
    [users.length],
  );

  useEffect(() => {
    if (fetchingUsers) return;
    const unfetchedUserIds = getListOfUnfetchedUsers(
      userDict,
      otherChannelMembers,
    );
    if (unfetchedUserIds.length > 0) {
      fetchUsers(unfetchedUserIds);
    }
  }, [userDict, otherChannelMembers, fetchUsers, fetchingUsers]);

  return (
    <div className={classNames("flex gap-4", className)}>
      <div>
        <ChannelAvatar loading={loadingAnyRelevantData} users={users} />
      </div>
      <div className="truncate">
        <ChannelTitle
          users={users}
          loading={loadingAnyRelevantData}
          translationMap={translationMap}
        />
        {lastMessageAt && (
          <Paragraph size="xxs" type="secondary">
            {lastMessageAt}
          </Paragraph>
        )}
      </div>
    </div>
  );
};

const EditMessage = ({
  translationMap,
}: {
  translationMap: { cancel: string; save: string; editMessage: string };
}) => {
  const { handleSubmit, clearEditingState } = useMessageInputContext();

  return (
    <div className="custom-edit-form w-full flex flex-col gap-6">
      <Headline className="self-center hidden sm:block" size={"l"}>
        {translationMap.editMessage}
      </Headline>
      <MessageInputFlat />
      <div className="flex gap-2">
        <Button
          className="flex-1"
          variant="secondary"
          text={translationMap.cancel}
          onClick={clearEditingState}
        />
        <Button
          className="flex-1"
          variant="primary"
          text={translationMap.save}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export const Chat = ({
  client,
  channelId,
  dateSettings,
  translationMap,
  inApp = false,
}: {
  client: StreamChat;
  channelId: string;
  dateSettings: DateSettings;
  translationMap: {
    noChat: string;
    cancel: string;
    save: string;
    editMessage: string;
  };
  inApp?: boolean;
}) => {
  const [loading, setLoading] = useState(true);
  const channel = client.channel("messaging", channelId);

  useEffect(() => {
    channel.watch().finally(() => setLoading(false));
    // This should only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StreamChatChannel
      LoadingIndicator={() => (
        <div className="flex items-center justify-center">
          <LoadingBounce />
        </div>
      )}
      channel={channel}
      DateSeparator={(props) => (
        <CustomDateSeparator {...props} dateSettings={dateSettings} />
      )}
      EditMessageInput={() => <EditMessage translationMap={translationMap} />}
    >
      <Window>
        {match({
          loading,
          noMessages: channel.state.messages.length === 0,
        })
          .with({ loading, noMessages: true }, () => (
            <div className="h-svh flex items-center justify-center">
              {loading ? (
                <LoadingBounce />
              ) : (
                <div className="flex flex-col items-center gap-10">
                  <Empty.F />
                  <Paragraph>{translationMap.noChat}</Paragraph>
                </div>
              )}
            </div>
          ))
          .otherwise(() => (
            <div className="flex flex-col flex-1 dark:bg-greyscale-900 overflow-y-scroll">
              <MessageList
                Message={(props) => (
                  <MessageSimple {...props} dateSettings={dateSettings} />
                )}
                messageActions={["edit", "delete", "react", "markUnread"]}
              />
            </div>
          ))}
        <div
          className={classNames(
            "border-t border-greyscale-200 dark:border-transparent",
            {
              "main-message-wrapper": inApp,
            },
          )}
        >
          <MessageInput grow maxRows={5} />
        </div>
      </Window>
    </StreamChatChannel>
  );
};
